/**
 * Admin Screen - MOBILE ONLY
 * 
 * System administration dashboard with real statistics.
 * Role-gated - redirects non-admins.
 * Stacked flow - back button, no bottom tabs.
 */

import React, { useCallback } from 'react';
import { View, Text, ScrollView, StyleSheet, Pressable, RefreshControl, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useRouter, Redirect } from 'expo-router';
import { useAuthStore } from '../../store/authStore';
import { useAdmin } from '../../hooks/useAdmin';
import { color, text, space, radius, touch, icon, layout } from '../../shared/tokens/mobile';
import { ScreenContainer, SectionHeader, ListCard, ListRow } from '../../components/v2/mobile';
import { formatCurrency } from '../../shared/utils/formatters';

export default function AdminScreen() {
  const router = useRouter();
  const user = useAuthStore((state) => state.user);
  const isAdmin = user?.role === 'admin';
  const { stats, pendingRequests, loading, error, refresh } = useAdmin();

  const handleBack = useCallback(() => router.back(), [router]);

  // Redirect non-admins
  if (!isAdmin) {
    return <Redirect href="/v2/home" />;
  }

  return (
    <ScreenContainer>
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        refreshControl={<RefreshControl refreshing={loading} onRefresh={refresh} tintColor={color.primary} />}
      >
        {/* HEADER */}
        <View style={styles.header}>
          <Pressable onPress={handleBack} style={styles.backButton} accessibilityRole="button">
            <Ionicons name="chevron-back" size={icon.lg} color={color.textPrimary} />
          </Pressable>
          <Text style={styles.title}>Admin</Text>
          <View style={styles.spacer} />
        </View>

        {/* ERROR STATE */}
        {error && (
          <View style={styles.errorBanner}>
            <Ionicons name="warning-outline" size={icon.sm} color={color.error} />
            <Text style={styles.errorText}>{error}</Text>
          </View>
        )}

        {/* LOADING STATE */}
        {loading && !stats ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={color.primary} />
          </View>
        ) : stats ? (
          <>
            {/* TOP KPI STATS */}
            <View style={styles.statsRow}>
              <StatCard
                value={stats.users.total_users}
                label="Users"
                delta={stats.users.new_users_30d > 0 ? `+${stats.users.new_users_30d} this month` : undefined}
              />
              <StatCard
                value={pendingRequests}
                label="Pending"
                highlight={pendingRequests > 0}
              />
              <StatCard
                value={stats.portfolios.total_portfolios}
                label="Portfolios"
                delta={stats.portfolios.new_portfolios_30d > 0 ? `+${stats.portfolios.new_portfolios_30d}` : undefined}
              />
            </View>

            {/* PROPERTY & FINANCIAL STATS */}
            <View style={styles.statsRow}>
              <StatCard
                value={stats.properties.total_properties}
                label="Opportunities"
                delta={stats.properties.new_properties_30d > 0 ? `+${stats.properties.new_properties_30d}` : undefined}
              />
              <StatCard
                value={stats.users.active_users_30d}
                label="Active (30d)"
              />
              <StatCard
                value={formatCurrency(stats.properties.total_property_value) || '$0'}
                label="Total Value"
                isText
              />
            </View>

            {/* USER MANAGEMENT */}
            <View style={styles.section}>
              <SectionHeader label="User Management" />
              <ListCard>
                <ListRow
                  iconName="people-outline"
                  title="All Users"
                  subtitle={`${stats.users.total_users} registered • ${stats.users.admin_count} admin`}
                  onPress={() => router.push('/v2/admin/users' as any)}
                />
                <ListRow
                  iconName="person-add-outline"
                  title="Account Requests"
                  subtitle={pendingRequests > 0 ? `${pendingRequests} pending approval` : 'No pending requests'}
                  onPress={() => router.push('/v2/admin/requests' as any)}
                  badgeCount={pendingRequests}
                  hideBorder
                />
              </ListCard>
            </View>

            {/* SYSTEM */}
            <View style={styles.section}>
              <SectionHeader label="System" />
              <ListCard>
                <ListRow
                  iconName="analytics-outline"
                  title="Usage Analytics"
                  subtitle={`${stats.users.active_users_30d} active users this month`}
                  onPress={() => router.push('/v2/admin/analytics' as any)}
                />
                <ListRow
                  iconName="chatbubbles-outline"
                  title="Feedback"
                  subtitle="Review user feedback"
                  onPress={() => router.push('/v2/admin/feedback' as any)}
                  hideBorder
                />
              </ListCard>
            </View>

            {/* DATA */}
            <View style={styles.section}>
              <SectionHeader label="Data" />
              <ListCard>
                <ListRow
                  iconName="business-outline"
                  title="Opportunities"
                  subtitle={`${stats.properties.owned_properties} owned • ${stats.properties.under_contract_properties} under contract`}
                  onPress={() => {}}
                  showChevron={false}
                />
                <ListRow
                  iconName="map-outline"
                  title="Coverage Areas"
                  subtitle="Manage supported counties"
                  onPress={() => router.push('/v2/admin/coverage' as any)}
                  hideBorder
                />
              </ListCard>
            </View>
          </>
        ) : null}
      </ScrollView>
    </ScreenContainer>
  );
}

// =============================================================================
// STAT CARD
// =============================================================================

interface StatCardProps {
  value: number | string;
  label: string;
  highlight?: boolean;
  delta?: string;
  isText?: boolean;
}

function StatCard({ value, label, highlight, delta, isText }: StatCardProps) {
  const displayValue = isText ? value : typeof value === 'number' ? value.toLocaleString() : value;
  
  return (
    <View style={[styles.statCard, highlight && styles.statCardHighlight]}>
      <Text style={[styles.statValue, highlight && styles.statValueHighlight]}>{displayValue}</Text>
      <Text style={styles.statLabel}>{label}</Text>
      {delta && <Text style={styles.statDelta}>{delta}</Text>}
    </View>
  );
}

// =============================================================================
// STYLES
// =============================================================================

const styles = StyleSheet.create({
  scroll: { flex: 1 },
  scrollContent: { paddingBottom: layout.scrollBottomPadding },
  header: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: space.sm, paddingVertical: space.sm },
  backButton: { width: touch.minimum, height: touch.minimum, alignItems: 'center', justifyContent: 'center' },
  title: { flex: 1, fontSize: text.body.fontSize, fontWeight: text.bodyMedium.fontWeight, color: color.textPrimary, textAlign: 'center' },
  spacer: { width: touch.minimum },
  section: { paddingHorizontal: space.md, marginBottom: space.lg },
  loadingContainer: { flex: 1, paddingVertical: space.xxl * 2, alignItems: 'center', justifyContent: 'center' },
  errorBanner: { flexDirection: 'row', alignItems: 'center', gap: space.sm, marginHorizontal: space.md, marginBottom: space.md, padding: space.md, backgroundColor: color.errorLight, borderRadius: radius.md },
  errorText: { flex: 1, fontSize: text.caption.fontSize, color: color.error },
  statsRow: { flexDirection: 'row', paddingHorizontal: space.md, marginBottom: space.md, gap: space.sm },
  statCard: { flex: 1, backgroundColor: color.surface, borderRadius: radius.md, padding: space.md, alignItems: 'center', borderWidth: 1, borderColor: color.border },
  statCardHighlight: { backgroundColor: color.warningLight, borderColor: color.warning },
  statValue: { fontSize: text.sectionTitle.fontSize, fontWeight: text.sectionTitle.fontWeight, color: color.textPrimary },
  statValueHighlight: { color: color.warning },
  statLabel: { fontSize: text.caption.fontSize, color: color.textSecondary, marginTop: space.xs },
  statDelta: { fontSize: text.caption.fontSize - 1, color: color.success, marginTop: 2 },
});
